import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import {findNodeHandle, useWindowDimensions, View} from 'react-native'
import {
  type AppBskyFeedDefs,
  moderatePost,
  type ModerationDecision,
} from '@atproto/api'
import {useLingui} from '@lingui/react/macro'
import {useNavigation} from '@react-navigation/native'

import {useBottomBarOffset} from '#/lib/hooks/useBottomBarOffset'
import {type NavigationProp} from '#/lib/routes/types'
import {
  announcementIsForMember,
  type Classroom,
  type ClassroomContent,
  type ClassroomLevelArchive,
  classroomMemberRole,
  contentFromAnotherLevel,
  type ContentKind,
  isClassroomExclusive,
  levelArchiveGroups,
  useClassroomActor,
} from '#/state/classrooms'
import {useModerationOpts} from '#/state/preferences/moderation-opts'
import {
  useClassroomPostViews,
  usePostViewsByUri,
} from '#/state/queries/classroom-posts'
import {useClassroomContentQuery} from '#/state/queries/classrooms'
import {type ComposerOpts} from '#/state/shell/composer'
import {ComposerPrompt} from '#/view/com/feeds/ComposerPrompt'
import {Post} from '#/view/com/post/Post'
import {List, type ListRef} from '#/view/com/util/List'
import {atoms as a, useBreakpoints, useTheme} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {EditBig_Stroke1_Corner0_Rounded as EditIcon} from '#/components/icons/EditBig'
import {Text} from '#/components/Typography'
import {
  AddCapsuleCard,
  AddFlashbookCard,
  AddModuleCard,
  CapsuleCell,
  FlashbookCell,
  ModuleCell,
  workColumns,
  WorkGridRow,
  type WorkRow,
  workRowKey,
  workRows,
} from '#/components/work'
import {IS_IOS} from '#/env'
import {ClassroomCapsuleCard} from './ClassroomCapsuleCard'
import {
  ClassroomContentCard,
  ClassroomLevelArchiveCard,
} from './ClassroomContentCard'
import {ClassroomOnlyBadge} from './ClassroomOnlyBadge'

interface SectionRef {
  scrollToTop: () => void
}

/*
 * The grid a tab's work is drawn in is `#/components/work`: a cell is a piece of
 * work's card or the card that adds one, one row builder lays out every work grid in
 * the app, and the cards both kinds of work are drawn by live there as well. It is
 * shared with a creator's own profile (see `#/screens/Profile/Sections/Work`), which
 * is why this file no longer owns any of it.
 */

/**
 * One tab of a classroom's work.
 *
 * What a classroom holds is posts — the same posts that live on the profiles of
 * the people who wrote them — so the **Posts** tab is a feed of real Taderok
 * posts and not a list of classroom records. A capsule is one of those posts, so
 * it is here too, exactly as it is on the profile that published it. They carry
 * everything a post carries: the comment count, the reactions, the menu, and a
 * tap that opens the thread. A reader comments and reacts in a classroom exactly
 * as they do anywhere else.
 *
 * The **Capsules**, **Modules** and **Flashbooks** tabs are that same work again,
 * each read the way the app reads that kind of work at the front: a two-up grid of
 * cards, three-up on a wide screen. A capsule is something to play and a book is something
 * to read, and a grid is how a person picks one — the cover, what it is for, how much
 * work is in it, and the way in. The two grids are one mechanism with two cards,
 * because the thing that makes a grid a grid is not which card sits in the cell.
 *
 * The exception is the seeded demo content, which was never published and so has
 * no post behind it: its cell keeps the card's shape and says it has nothing to
 * play, because a grid cell that turns into a paragraph is not a grid. A capsule
 * made *for this room* is the opposite of an exception: it is a post, and its cell
 * carries the badge that says the room is the only place it lives (see
 * `ClassroomCapsuleCard`, and `FlashbookCell` for the same badge on a book). A
 * book is never post-less at all — a book with no post is a draft, and a draft
 * is not in anybody's classroom.
 *
 * Taking a post out of the room is not a control of its own. A post is a post, so
 * its own menu carries every action it has, and the room adds one item to that
 * menu rather than a second button of its own beside it — which is why the grid's
 * cards, which have no menu, do not offer removal.
 */
export function ClassroomContentSection({
  ref,
  classroom,
  kind,
  canPost,
  headerHeight,
  isFocused,
  scrollElRef,
  setScrollViewTag,
  onPost,
  onAddCapsule,
  onAddFlashbook,
  onAddModule,
  onRemove,
}: {
  ref?: React.Ref<SectionRef>
  classroom: Classroom
  kind: ContentKind
  canPost: boolean
  headerHeight: number
  isFocused: boolean
  scrollElRef: ListRef
  setScrollViewTag: (tag: number | null) => void
  /** Where a post written from this tab's own row lands. */
  onPost: NonNullable<ComposerOpts['onPost']>
  onAddCapsule: () => void
  onAddFlashbook: () => void
  onAddModule: () => void
  onRemove: (id: string) => void
}) {
  const t = useTheme()
  const {height} = useWindowDimensions()
  const bottomBarOffset = useBottomBarOffset(100)
  const {t: l} = useLingui()
  const actor = useClassroomActor()
  const {gtMobile} = useBreakpoints()

  /** The room, in the shape the composer takes it, for this tab's own row. */
  const classroomRef = useMemo(
    () => ({
      id: classroom.id,
      name: classroom.name,
      handle: classroom.handle,
      ownerHandle: classroom.ownerHandle,
      ownerDid: classroom.ownerDid,
      visibility: classroom.visibility,
    }),
    [
      classroom.handle,
      classroom.id,
      classroom.name,
      classroom.ownerDid,
      classroom.ownerHandle,
      classroom.visibility,
    ],
  )

  /*
   * What this tab holds, as the server answers it: only what this reader may
   * see. The Posts tab is the room entire (a capsule and a book are both posts),
   * and each other tab is one kind of it.
   */
  const contentQuery = useClassroomContentQuery(
    classroom.id,
    kind === 'post' ? undefined : kind,
  )
  const items = contentQuery.data?.pages.flatMap(page => page.content) ?? []
  const onEndReached = () => {
    if (contentQuery.hasNextPage && !contentQuery.isFetchingNextPage) {
      void contentQuery.fetchNextPage()
    }
  }
  const [isRefreshing, setIsRefreshing] = useState(false)
  const onRefresh = async () => {
    setIsRefreshing(true)
    try {
      await contentQuery.refetch()
    } finally {
      setIsRefreshing(false)
    }
  }
  /*
   * A visitor of a private room who is shown nothing is told the work is for
   * its members, rather than that the room is empty.
   */
  const role = classroomMemberRole(classroom, actor)
  const membersOnly =
    role === null &&
    classroom.visibility === 'private' &&
    contentQuery.isSuccess &&
    items.length === 0

  /*
   * What a change of level does to a room's work, in one place.
   *
   * The work already in the room keeps the level it was written at, so the room
   * holds two kinds of thing the moment it moves: what it teaches now, and the
   * periods it has left behind. Neither is deleted. The period *posts* are folded
   * away behind a divider that says what happened (`ClassroomLevelArchiveCard`),
   * because a room's Posts tab is its history and a school that moved its level by
   * mistake has to be able to read back what it moved away from. The same work
   * with a level of its own is **hidden** in the three grids — a capsule is
   * something you play at the level you are studying, and a grid of unplayable
   * work is not a grid — and comes back by itself when the room returns to that
   * level, because none of this is a flag: the tests are asked again on every
   * render (`contentFromAnotherLevel`).
   */
  const shownItems = useMemo(
    () =>
      kind === 'post'
        ? items
        : items.filter(item => !contentFromAnotherLevel(item, classroom)),
    [classroom, items, kind],
  )

  /**
   * The announcements this viewer is the audience for — pinned above the rest.
   *
   * A level change is the room's standing news for the members it leaves behind,
   * and news that has to be found halfway down a feed is not pinned at all. It is
   * the room's *first* card for them, above its own composer row, until they answer
   * it — by moving their level with the room, by leaving it, or by keeping it as it
   * is (`announcementIsForMember`).
   */
  const pinned = useMemo(
    () => items.filter(item => announcementIsForMember(item, classroom, actor)),
    [actor, classroom, items],
  )

  /** One row of the Posts tab: a pinned announcement, a post, or a folded period. */
  type PostRow =
    | {kind: 'post'; item: ClassroomContent}
    | {kind: 'archive'; group: ClassroomLevelArchive<ClassroomContent>}

  const [openArchives, setOpenArchives] = useState<Record<string, boolean>>({})

  const postRows = useMemo<PostRow[]>(() => {
    const pinnedIds = new Set(pinned.map(item => item.id))
    /*
     * An announcement is never one of the room's ordinary posts. It is either
     * drawn as the pin — when this reader is one of the members it concerns — or
     * not drawn at all: a member who studies what the room teaches now has
     * nothing to answer, and the card would be one they had to dismiss for no
     * reason. Past announcements go the same way rather than into the archive,
     * because an announcement is not a piece of work the room made.
     */
    const rest = items.filter(
      item => !pinnedIds.has(item.id) && !item.announcement,
    )
    return [
      ...rest
        .filter(item => !contentFromAnotherLevel(item, classroom))
        .map(item => ({kind: 'post' as const, item})),
      ...levelArchiveGroups(classroom, rest).map(group => ({
        kind: 'archive' as const,
        group,
      })),
    ]
  }, [classroom, items, pinned])

  const postUris = useMemo(
    () => items.map(item => item.postUri).filter((uri): uri is string => !!uri),
    [items],
  )
  const {data: postViews, isPending, isError} = useClassroomPostViews(postUris)
  const viewsByUri = usePostViewsByUri(postViews)

  /**
   * Moderation for the real posts, once for the whole list.
   *
   * A capsule's card and a book's card are both drawn through `Hider`, so each
   * needs a decision like any other piece of content — and the posts arrive in a
   * batch, so it is cheaper to decide them together than to ask the same question
   * per card.
   */
  const moderationOpts = useModerationOpts()
  const moduiByUri = useMemo(() => {
    const map = new Map<string, ModerationDecision>()
    if (!moderationOpts) return map
    for (const post of postViews ?? []) {
      map.set(post.uri, moderatePost(post, moderationOpts))
    }
    return map
  }, [moderationOpts, postViews])

  /** Whether the server lets this reader take the entry out of the room. */
  const canRemoveFor = useCallback(
    (entry: ClassroomContent) => !!entry.viewerCanManage,
    [],
  )

  useImperativeHandle(ref, () => ({
    scrollToTop: () => {},
  }))

  useEffect(() => {
    if (IS_IOS && isFocused && scrollElRef.current) {
      const nativeTag = findNodeHandle(scrollElRef.current)
      setScrollViewTag(nativeTag)
    }
  }, [isFocused, scrollElRef, setScrollViewTag])

  const renderItem = useCallback(
    ({item, index}: {item: ClassroomContent; index: number}) => {
      if (item.postUri && !viewsByUri.has(item.postUri)) {
        /*
         * Still fetching, or the app could not ask, or the post was deleted
         * where it lived.
         *
         * Only the last of those three is about the post. A request that never
         * came back is about the *reading* — the app does not know what is in
         * the room's list any more than it did before it asked — so a post whose
         * fetch failed is not called gone; it says what happened and the room
         * keeps its work rather than accusing it (`isError`). A post that really
         * is gone says so rather than silently leaving a hole in the room.
         */
        if (isPending) return <View style={{minHeight: 56}} />
        if (isError) {
          return (
            <View
              testID={`classroomContentUnreachable-${item.id}`}
              style={[
                a.px_lg,
                a.py_lg,
                index !== 0 && a.border_t,
                t.atoms.border_contrast_low,
              ]}>
              <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
                {l`This post could not be loaded. Try again in a moment.`}
              </Text>
            </View>
          )
        }
        return (
          <View
            testID={`classroomContentGone-${item.id}`}
            style={[
              a.px_lg,
              a.py_lg,
              a.flex_row,
              a.align_center,
              a.gap_md,
              index !== 0 && a.border_t,
              t.atoms.border_contrast_low,
            ]}>
            <Text style={[a.text_md, a.flex_1, t.atoms.text_contrast_medium]}>
              {l`This post is no longer here.`}
            </Text>
            {canRemoveFor(item) ? (
              <Button
                testID={`classroomRemoveContent-${item.id}`}
                label={l`Take this out of the classroom`}
                color="secondary"
                size="small"
                onPress={() => onRemove(item.id)}>
                <ButtonText>{l`Remove`}</ButtonText>
              </Button>
            ) : null}
          </View>
        )
      }
      const view = item.postUri ? viewsByUri.get(item.postUri) : undefined
      if (view) {
        return (
          <View testID={`classroomContent-${item.id}`}>
            <Post
              post={view}
              hideTopBorder={index === 0}
              inClassroom
              classroomAction={
                canRemoveFor(item)
                  ? {onRemoveFromClassroom: () => onRemove(item.id)}
                  : undefined
              }
            />
          </View>
        )
      }
      return (
        <View
          style={[
            a.px_lg,
            a.py_lg,
            index !== 0 && a.border_t,
            t.atoms.border_contrast_low,
          ]}>
          <ClassroomContentCard
            classroom={classroom}
            content={item}
            onRemove={onRemove}
          />
        </View>
      )
    },
    [
      classroom,
      canRemoveFor,
      isError,
      isPending,
      l,
      onRemove,
      t.atoms.border_contrast_low,
      viewsByUri,
    ],
  )

  /**
   * The grid's rows (the layout itself is `#/components/work`).
   *
   * The way in comes first and the work after it, and the columns are decided once
   * for the whole grid rather than per row, so the last row is short rather than
   * wrong. Every cell fills the grid: an entry with no playable post keeps the card's
   * shape and says what it is, so the grid stays a grid and no cell has to be given
   * the whole width to stay readable.
   *
   * The way in is a cell of its own, and it is there when the room holds work of
   * this kind and the viewer can post — the two conditions the button above the
   * grid had, so nothing a teacher could reach before is out of reach now. A room
   * with nothing in it still says so in its own words, with its own two buttons
   * (`Empty`).
   */
  const gridRows = useMemo(
    () =>
      workRows(shownItems, {
        columns: workColumns(gtMobile),
        keyOf: entry => entry.id,
        withAdd: canPost && shownItems.length > 0,
      }),
    [canPost, gtMobile, shownItems],
  )

  const renderGridRow = useCallback(
    ({item: row}: {item: WorkRow<ClassroomContent>; index: number}) => (
      <WorkGridRow
        cells={row.cells}
        renderAdd={() =>
          kind === 'capsule' ? (
            <AddCapsuleCard
              testID="classroomCapsulesAdd"
              label={l`Add a capsule`}
              caption={l`From your capsules, or make a new one.`}
              onPress={onAddCapsule}
            />
          ) : kind === 'module' ? (
            <AddModuleCard
              testID="classroomModulesAdd"
              label={l`Build a module`}
              caption={l`Gather six capsules from the room, in order.`}
              onPress={onAddModule}
            />
          ) : (
            <AddFlashbookCard
              testID="classroomFlashbooksAdd"
              label={l`Add a FlashBook`}
              caption={l`From your books, or write a new one.`}
              onPress={onAddFlashbook}
            />
          )
        }
        renderWork={entry =>
          kind === 'capsule' ? (
            <ClassroomCapsuleCell
              entry={entry}
              post={entry.postUri ? viewsByUri.get(entry.postUri) : undefined}
              modui={entry.postUri ? moduiByUri.get(entry.postUri) : undefined}
              isPending={isPending}
            />
          ) : kind === 'module' ? (
            <ClassroomModuleCell
              entry={entry}
              post={entry.postUri ? viewsByUri.get(entry.postUri) : undefined}
              isPending={isPending}
            />
          ) : (
            <ClassroomFlashbookCell
              entry={entry}
              post={entry.postUri ? viewsByUri.get(entry.postUri) : undefined}
              isPending={isPending}
            />
          )
        }
      />
    ),
    [
      isPending,
      kind,
      l,
      moduiByUri,
      onAddCapsule,
      onAddFlashbook,
      onAddModule,
      viewsByUri,
    ],
  )

  const empty = (
    <Empty
      kind={kind}
      classroomId={classroom.id}
      visibility={classroom.visibility}
      membersOnly={membersOnly}
      canPost={canPost}
      onAddCapsule={onAddCapsule}
      onAddFlashbook={onAddFlashbook}
      onAddModule={onAddModule}
    />
  )

  const contentStyle = {
    minHeight: height + headerHeight,
    paddingBottom: bottomBarOffset,
  }

  if (kind !== 'post') {
    return (
      <View testID={`classroomContentSection-${kind}`}>
        <List
          testID={`classroomContentList-${kind}`}
          ref={scrollElRef}
          data={gridRows}
          renderItem={renderGridRow}
          keyExtractor={workRowKey}
          headerOffset={headerHeight}
          contentContainerStyle={contentStyle}
          removeClippedSubviews
          desktopFixedHeight
          ListEmptyComponent={empty}
          onEndReached={onEndReached}
          onRefresh={onRefresh}
          refreshing={isRefreshing}
        />
      </View>
    )
  }

  const renderRow = ({item: row, index}: {item: PostRow; index: number}) =>
    row.kind === 'archive' ? (
      <ClassroomLevelArchiveCard
        group={row.group}
        expanded={!!openArchives[row.group.key]}
        onToggle={() =>
          setOpenArchives(open => ({
            ...open,
            [row.group.key]: !open[row.group.key],
          }))
        }
        renderItem={(entry, index) => renderItem({item: entry, index})}
      />
    ) : (
      renderItem({item: row.item, index})
    )

  return (
    <View testID="classroomContentSection-post">
      <List
        testID="classroomContentList-post"
        ref={scrollElRef}
        data={postRows}
        renderItem={renderRow}
        keyExtractor={row =>
          row.kind === 'archive' ? row.group.key : row.item.id
        }
        headerOffset={headerHeight}
        contentContainerStyle={contentStyle}
        removeClippedSubviews
        desktopFixedHeight
        ListEmptyComponent={empty}
        /*
         * The pin, and the room's composer row under it.
         *
         * The announcements come first because that is what *pinned* means — the
         * one card a member has to read to know what happened to their room — and
         * the composer row follows, so the top of a room reads as: what changed,
         * then what you can say about it.
         *
         * The row is the one the feeds have, shown to everyone who may post here
         * (the owner and its teachers), including in an empty room: it is the
         * desktop's way to write in the room, where the round button is not
         * drawn. It carries the room, so the post is written into it exactly as
         * the round button's is.
         */
        ListHeaderComponent={
          pinned.length > 0 || canPost ? (
            <>
              {pinned.map((item, index) => (
                <View key={item.id}>{renderItem({item, index})}</View>
              ))}
              {canPost ? (
                /*
                 * The row draws no border of its own: in a feed the first
                 * post's top border is the line under it. An empty room has
                 * no first post, so the row closes itself.
                 */
                <View
                  style={
                    postRows.length === 0 && [
                      a.border_b,
                      t.atoms.border_contrast_low,
                    ]
                  }>
                  <ComposerPrompt
                    testID="classroomComposerPrompt"
                    classroom={classroomRef}
                    onPost={onPost}
                    logContext="Other"
                  />
                </View>
              ) : null}
            </>
          ) : undefined
        }
        onEndReached={onEndReached}
        onRefresh={onRefresh}
        refreshing={isRefreshing}
      />
    </View>
  )
}

/**
 * One capsule cell in a room: the app's capsule card, plus the two things only a
 * room has to say about one.
 *
 * What the room adds to `CapsuleCell`: the **badge**, because a capsule written for
 * this room is on no profile and in no feed afterwards (see `ClassroomOnlyBadge`),
 * and the **post-less cell**, because a capsule made for a room *before* those were
 * published as posts has no post to play it from and only this room can show it at
 * all (`ClassroomCapsuleCard`).
 */
function ClassroomCapsuleCell({
  entry,
  post,
  modui,
  isPending,
}: {
  entry: ClassroomContent
  post: AppBskyFeedDefs.PostView | undefined
  modui: ModerationDecision | undefined
  isPending: boolean
}) {
  return (
    <CapsuleCell
      entry={entry}
      post={post}
      modui={modui}
      isPending={isPending}
      badge={isClassroomExclusive(entry) ? <ClassroomOnlyBadge /> : null}
      goneTestID={`classroomCapsuleGone-${entry.id}`}
      unpublishedTestID={`classroomCapsulePreview-${entry.id}`}
      renderPostless={capsuleUri => (
        <View testID={`classroomCapsuleExclusive-${entry.id}`}>
          <ClassroomCapsuleCard entry={entry} capsuleUri={capsuleUri} />
        </View>
      )}
    />
  )
}

/**
 * One module cell in a room: the shared playlist card, and the badge when the room is
 * the only place the playlist lives.
 *
 * A module has no post-less state of its own the way a capsule does — a module with
 * no post is a draft, which never reaches a room — so there is nothing for a room to
 * draw here that a profile's grid does not already draw, and this wrapper exists for
 * the badge and the testID exactly as the book's does.
 */
function ClassroomModuleCell({
  entry,
  post,
  isPending,
}: {
  entry: ClassroomContent
  post: AppBskyFeedDefs.PostView | undefined
  isPending: boolean
}) {
  return (
    <ModuleCell
      entry={entry}
      post={post}
      isPending={isPending}
      testID={`classroomModule-${entry.id}`}
      badge={isClassroomExclusive(entry) ? <ClassroomOnlyBadge /> : null}
      goneTestID={`classroomModuleGone-${entry.id}`}
      unpublishedTestID={`classroomModuleUnpublished-${entry.id}`}
    />
  )
}

/**
 * One book cell in a room: the app's book card, and the badge when the room is the
 * only place the book lives.
 *
 * A book has no third state the way a capsule does — a book with no post is a draft,
 * and a draft is never written into a room — so there is no post-less cell to reach
 * for here, and `FlashbookCell` says so in its own words if it ever meets one.
 */
function ClassroomFlashbookCell({
  entry,
  post,
  isPending,
}: {
  entry: ClassroomContent
  post: AppBskyFeedDefs.PostView | undefined
  isPending: boolean
}) {
  return (
    <FlashbookCell
      entry={entry}
      post={post}
      isPending={isPending}
      testID={`classroomFlashbook-${entry.id}`}
      badge={
        isClassroomExclusive(entry) ? (
          <ClassroomOnlyBadge belowKindChip />
        ) : null
      }
      goneTestID={`classroomFlashbookGone-${entry.id}`}
      unpublishedTestID={`classroomFlashbookUnpublished-${entry.id}`}
    />
  )
}

function Empty({
  kind,
  classroomId,
  visibility,
  membersOnly,
  canPost,
  onAddCapsule,
  onAddFlashbook,
  onAddModule,
}: {
  kind: ContentKind
  classroomId: string
  /** Which door this room has, for the line that says how to get past it. */
  visibility: 'public' | 'private'
  /** The reader is outside a private room and was shown none of its work. */
  membersOnly: boolean
  canPost: boolean
  onAddCapsule: () => void
  onAddFlashbook: () => void
  onAddModule: () => void
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const navigation = useNavigation<NavigationProp>()

  if (membersOnly) {
    return (
      <View
        testID="classroomHiddenContent"
        style={[a.px_lg, a.py_4xl, a.align_center, a.gap_sm]}>
        <Text style={[a.text_md, a.text_center, t.atoms.text_contrast_high]}>
          {l`The work in this classroom is for its members.`}
        </Text>
        {/*
          How to get in, in the room's own terms. This line used to say *join with
          the code* to every room, including the public ones that have no code and
          whose page offers none — the same broken promise the header menu was
          fixed for. A public classroom is asked into; that is the whole of its
          door.
        */}
        <Text style={[a.text_sm, a.text_center, t.atoms.text_contrast_medium]}>
          {visibility === 'private'
            ? l`Join with the code you were given to read it.`
            : l`Ask to join to read it.`}
        </Text>
      </View>
    )
  }

  return (
    <View style={[a.px_lg, a.py_4xl, a.align_center, a.gap_md]}>
      <EditIcon size="3xl" fill={t.atoms.text_contrast_medium.color} />
      <Text style={[a.text_md, a.text_center, t.atoms.text_contrast_high]}>
        {kind === 'capsule'
          ? l`No capsules in this classroom yet.`
          : kind === 'module'
            ? l`No modules in this classroom yet.`
            : kind === 'flashbook'
              ? l`No books in this classroom yet.`
              : l`Nothing has been posted here yet.`}
      </Text>
      {/*
        Capsules, modules and books each offer their two ways of adding one.

        A post offers none: writing a post has its own button on the page — the
        FAB, which is there on every tab a poster is reading — and an empty
        Posts tab used to draw a second *Write a post* under the first line of
        copy, which is the same door twice and makes the empty state look like a
        menu rather than a state. The other kinds have no FAB of their own, so
        their two buttons stay where the reader is looking.
      */}
      {canPost && kind !== 'post' ? (
        <View style={[a.flex_row, a.gap_sm]}>
          <Button
            testID={`classroomEmptyCompose-${kind}`}
            label={
              kind === 'capsule'
                ? l`Put a capsule in this classroom`
                : kind === 'module'
                  ? l`Build a module for this classroom`
                  : l`Put a book in this classroom`
            }
            color="primary"
            size="small"
            onPress={
              kind === 'capsule'
                ? onAddCapsule
                : kind === 'module'
                  ? onAddModule
                  : onAddFlashbook
            }>
            <ButtonIcon icon={EditIcon} />
            <ButtonText>
              {kind === 'capsule'
                ? l`Add a capsule`
                : kind === 'module'
                  ? l`Build a module`
                  : l`Add a book`}
            </ButtonText>
          </Button>
          <Button
            testID="classroomEmptyOpenStudio"
            label={
              kind === 'module'
                ? l`Open the module builder`
                : kind === 'flashbook'
                  ? l`Open the book studio`
                  : l`Open the capsule studio`
            }
            color="secondary"
            size="small"
            onPress={() =>
              /*
               * The module builder opens *attached to the room*, which is the
               * one thing that separates a room's module from a profile's: it is
               * where the capsules are chosen, and publishing it puts it in the
               * classroom and comes back to it (see `#/screens/ModuleStudio`).
               */
              navigation.navigate(
                kind === 'module'
                  ? 'ModuleStudio'
                  : kind === 'flashbook'
                    ? 'FlashBookStudio'
                    : 'CapsuleStudio',
                {classroomId},
              )
            }>
            <ButtonText>{l`Studio`}</ButtonText>
          </Button>
        </View>
      ) : null}
    </View>
  )
}
